import React, { useEffect, useState } from "react";
import {
  Dropdown,
  DropdownButton,
  DropdownItem,
  DropdownMenu,
} from "../../common/dropdown";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../common/table";
import {
  Alert,
  AlertActions,
  AlertDescription,
  AlertTitle,
} from "../../common/alert";
import { Button } from "../../common/button";
import { message } from "antd";
import Empty from "../../images/empty.svg";
import {
  PlusIcon,
  TrashIcon,
  ArrowPathIcon,
} from "@heroicons/react/24/outline";
import {
  Dialog,
  DialogBody,
  DialogTitle,
  DialogActions,
} from "V2.0/common/dialog";
import { DialogTitleWithClose } from "../HomeModule/CustomAPIs/components/Dialog/DialogTitleWithClose";
import AddSpreadsheet from "../HomeKBComponent/AddSpreadsheet";
import {
  deleteCorpus,
  syncGSheet,
  updateGSheetIntervalSettings,
} from "./service";
import Joi from "joi-browser";
import { useForm } from "../Assistants/AssistantTools/CustomAPIs/components/Form/useForm";
import Form from "../Assistants/AssistantTools/CustomAPIs/components/Form";
import { showErrorToast } from "V2.0/utils";

function SpreadSheetAutoSync(props) {
  const { item, onClose, workspaceId, kbId, fetchKb } = props;
  const [loading, setLoading] = useState(false);

  const inputField = {
    syncEnabled: item?.syncEnabled || false,
    syncAtInterval: Math.floor(item?.syncAtInterval / (60 * 60)) || 0,
  };

  const template = [
    {
      name: "syncEnabled",
      label: "Enable Auto-Sync",
      type: "toggle",
    },
    {
      name: "syncAtInterval",
      label: "Sync Interval (Hours)",
      type: "number",
    },
  ];

  const schema = {
    syncEnabled: Joi.boolean().label("Auto Sync"),
    syncAtInterval: Joi.number()
      .min(4)
      .label("Sync Interval")
      .options({
        language: {
          number: {
            min: "must be at least {{limit}} hours.",
          },
        },
      }),
  };

  const { data, validate, handleChange, error, setError } = useForm(
    inputField,
    schema,
    false
  );

  const handleSave = async () => {
    setLoading(true);
    message.loading("Updating settings");

    const payload = {
      ...data,
      syncAtInterval: data.syncAtInterval * 60 * 60,
    };

    try {
      const response = await updateGSheetIntervalSettings({
        workspaceId,
        kbId,
        eTag: item.eTag,
        payload,
      });

      if (response.status === 200) {
        setLoading(false);
        message.destroy();
        message.success("Settings updated");
        fetchKb();
        onClose();
      }
    } catch (e) {
      message.destroy();
      showErrorToast(e, "Failed to update settings");
      setLoading(false);
    }
  };

  return (
    <Dialog open={true} onClose={onClose}>
      <DialogTitle>Auto Sync Settings</DialogTitle>
      <DialogBody>
        <Form
          template={template}
          handleChange={handleChange}
          data={data}
          error={error}
        />
      </DialogBody>
      <DialogActions>
        <Button outline onClick={onClose}>
          Cancel
        </Button>
        <Button loading={loading} onClick={() => handleSave()}>
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function AddSpreadSheet(props) {
  const [open, setOpen] = useState(false);

  return (
    <>
      <Button outline onClick={() => setOpen(true)}>
        <PlusIcon />
        Add Spreadsheet
      </Button>
      <Dialog size="xl" open={open} onClose={() => {}}>
        <DialogTitle>
          <DialogTitleWithClose
            title="Add Spreadsheet"
            onClose={() => setOpen(false)}
          />
        </DialogTitle>
        <DialogBody>
          <AddSpreadsheet {...props} onClose={() => setOpen(false)} />
        </DialogBody>
      </Dialog>
    </>
  );
}

function Spreadsheets({
  KB,
  projects,
  activeProject,
  reload,
  setReload,
  refetch,
  isViewOnly
}) {
  let [isOpen, setIsOpen] = useState(false);
  const [eTag, setEtag] = useState("");
  const [loading, setLoading] = useState(false);
  const [openAutoSync, updateOpenAutoSync] = useState({
    isOpen: false,
    item: null,
  });

  const handleDelete = async () => {
    setLoading(true);
    try {
      const response = await deleteCorpus(
        KB._id,
        projects[activeProject]._id,
        eTag
      );

      if (response.status === 200) {
        message.success("Spreadsheet deleted");
        refetch(projects[activeProject]._id, KB._id);
        setIsOpen(false);
        setLoading(false);
        setReload(!reload);
      }
    } catch (e) {
      showErrorToast(e, "Failed to delete Spreadsheet");
      setIsOpen(false);
      setLoading(false);
    }
  };

  const handleGoogleSheetSync = async (item) => {
    setLoading(true);
    let data = {};
    // data.url = item.location;
    // data.template = "general";
    data.eTag = item.eTag;

    try {
      const response = await syncGSheet(
        KB._id,
        projects[activeProject]._id,
        data
      );

      if (response.status === 200) {
        message.success("The information will be updated shortly.");
      }
    } catch (e) {
      showErrorToast(e, "Failed to sync spreadsheet");
    }
  };

  const showDeleteModal = (etag) => {
    setIsOpen(true);
    setEtag(etag);
  };

  const Skeleton = () => {
    return (
      <div className="animate-pulse">
        <div className="h-4 bg-gray-200 rounded"></div>
      </div>
    );
  };

  return (
    <>
      <div className="flex flex-col max-h-[85dvh] p-8 gap-y-4">
        <div className="flex justify-between pb-4 border-b">
          <p className="text-xl font-semibold">Spreadsheets</p>
          {!isViewOnly && <AddSpreadSheet
            KB={KB}
            projects={projects}
            activeProject={activeProject}
            refetch={refetch}
          />}
        </div>
        <div className="border rounded-lg h-full p-4">
          <Table className="[--gutter:theme(spacing.6)] sm:[--gutter:theme(spacing.8)] max-h-[80dvh] overflow-scroll">
            <TableHead>
              <TableRow>
                <TableHeader>Data Type</TableHeader>
                <TableHeader>Content</TableHeader>
                <TableHeader>Status</TableHeader>
                <TableHeader>Actions</TableHeader>
              </TableRow>
            </TableHead>
            <TableBody>
              {KB && KB.corpus ? (
                KB.corpus.filter((corpus) => corpus.type === "google_sheet")
                  .length > 0 ? (
                  KB.corpus
                    .filter((corpus) => corpus.type === "google_sheet")
                    .map((item, index) => (
                      <TableRow key={index}>
                        <TableCell className="font-medium">
                          {item.type}
                        </TableCell>
                        <TableCell
                          className="truncate"
                          style={{ maxWidth: "200px" }}
                        >
                          {item.location}
                        </TableCell>
                        <TableCell className="text-zinc-500">
                          {item.state}
                        </TableCell>
                        <TableCell>
                          {!isViewOnly && <div className="flex gap-x-2">
                            <Button
                              outline
                              onClick={() => showDeleteModal(item.eTag)}
                            >
                              <TrashIcon />
                            </Button>
                            <Dropdown>
                              <DropdownButton outline aria-label="Sync">
                                <ArrowPathIcon />
                              </DropdownButton>
                              <DropdownMenu anchor="bottom start">
                                <DropdownItem
                                  onClick={() => handleGoogleSheetSync(item)}
                                >
                                  Sync Now
                                </DropdownItem>
                                <DropdownItem
                                  onClick={() => {
                                    updateOpenAutoSync({ isOpen: true, item });
                                  }}
                                >
                                  Configure Auto Sync
                                </DropdownItem>
                              </DropdownMenu>
                            </Dropdown>
                          </div>}
                        </TableCell>
                      </TableRow>
                    ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={4} className="text-center">
                      <div className="flex flex-col items-center justify-center m-5">
                        <img src={Empty} alt="No files" className="w-16 h-16" />
                        <p>You might not have uploaded any Spreadsheet yet.</p>
                      </div>
                    </TableCell>
                  </TableRow>
                )
              ) : (
                [...Array(3)].map((_, index) => (
                  <TableRow key={index}>
                    <TableCell>
                      <Skeleton />
                    </TableCell>
                    <TableCell>
                      <Skeleton />
                    </TableCell>
                    <TableCell>
                      <Skeleton />
                    </TableCell>
                    <TableCell>
                      <Skeleton />
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
        {isOpen && (
          <Alert open={isOpen} onClose={setIsOpen}>
            <AlertTitle>Delete Spreadsheet</AlertTitle>
            <AlertDescription>
              By deleting this file, your project will no longer have access to
              this knowledge.
            </AlertDescription>
            <AlertActions>
              <Button plain onClick={() => setIsOpen(false)}>
                Cancel
              </Button>
              <Button onClick={handleDelete}>
                {loading ? "Please wait..." : "Delete"}
              </Button>
            </AlertActions>
          </Alert>
        )}
      </div>
      {openAutoSync.isOpen && (
        <SpreadSheetAutoSync
          onClose={() => updateOpenAutoSync({ isOpen: false, item: null })}
          item={openAutoSync.item}
          workspaceId={projects[activeProject]?._id}
          kbId={KB._id}
          fetchKb={() => refetch(projects[activeProject]?._id, KB._id)}
        />
      )}
    </>
  );
}

export default Spreadsheets;
